import React, { useEffect, useRef } from 'react';
import { Camera, Images, X, AlertCircle, FileText } from 'lucide-react';
import { cn } from '@/lib/utils';
import { photoPickerReducer, esteDovadaPdf, numeDovada, type PickedPhoto, type PhotoAction } from './photo-picker.reducer';

export type { PickedPhoto } from './photo-picker.reducer';

export interface PhotoPickerProps {
  photos: PickedPhoto[];
  onChange: (photos: PickedPhoto[]) => void;
  upload: (file: File, onProgress: (pct: number) => void) => Promise<{ fileId: string }>;
  /**
   * implicit 5 — gandit pentru pagina publica de colaboratori (planul 05), care nu are
   * un contract de metadata proprii. `ImageUploadCompletion` (lectii) trece explicit
   * `metadata.maxPhotos`, cu implicit 4 din `DEFAULT_LIMITS.MAX_PHOTOS`. Cele doua cifre
   * (5 aici, 4 acolo) sunt deliberate, nu o inconsistenta de sincronizat.
   */
  max?: number;
  disabled?: boolean;
  /**
   * Implicit `false`. Cand e `true`, grila de miniaturi primeste `max-h` + `overflow-y-auto`
   * proprii, ca sa ramana derulabila independent de restul ecranului (F2.4, pagina publica de
   * colaborator, Task 14). E o EXCEPTIE de la R4 ("un singur scroll pe ecran") - de aceea e
   * optionala si default `false`: `ImageUploadCompletion` (fluxul de lectie) randeaza deja
   * `PhotoPicker` in interiorul containerului marcat "singurul scroll de pe ecran (R4)"
   * (`LessonPlayer.tsx`, `PreviewMode.tsx`) - un `max-h`/`overflow` necontional aici ar fi creat
   * un scroll imbricat acolo, silentios (incident: pagina publica a stricat fluxul de lectie).
   * Doar `StepPhotos.tsx` (pagina publica) trece `constrainGrid`; restul consumatorilor nu-l ating
   * si primesc exact comportamentul dinainte de Task 14.
   */
  constrainGrid?: boolean;
  /**
   * Implicit `false`. B3 (Task 15): pe calea profesorului („Încarcă poze") dovada poate fi si PDF.
   * Cu `true`: galeria accepta si `application/pdf`, un PDF apare ca tile cu icon + nume (nu `<img>`),
   * iar contorul spune „fișiere". Camera ramane doar pentru poze. Colaboratorul (`StepPhotos`) si
   * playerul de lectie (`ImageUploadCompletion`) nu-l trec, deci raman exact ca inainte.
   */
  acceptPdf?: boolean;
}

const ACCEPT = 'image/jpeg,image/png,image/webp';
const ACCEPT_CU_PDF = 'image/*,application/pdf';

/**
 * Selector de fotografii pentru telefon (wireframe 4.3, pasul 3):
 * două acțiuni explicite (cameră / galerie), progres și ✕ per miniatură, contor „N din maximum M".
 * Componentă controlată: starea e a părintelui, tranzițiile trec prin photoPickerReducer.
 */
export function PhotoPicker({ photos, onChange, upload, max = 5, disabled = false, constrainGrid = false, acceptPdf = false }: PhotoPickerProps) {
  const cameraRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);

  // Oglinda ultimei stări: upload-urile concurente emit progres pe closure-uri vechi.
  const latest = useRef(photos);
  latest.current = photos;
  const dispatch = (action: PhotoAction) => {
    const next = photoPickerReducer(latest.current, action);
    if (next !== latest.current) {
      latest.current = next;
      onChange(next);
    }
  };

  // I3 (final-review.md): blob URL-urile supravietuiesc navigarii SPA — doar un unload complet
  // le elibereaza. La un upload reusit, revocam blob-ul vechi (reducerul a mutat deja previewUrl
  // pe URL-ul remote in aceeasi tranzitie 'done', deci miniatura ramane vizibila). Cleanup pe
  // unmount acopera si pozele ramase 'pending'/'error' cand elevul navigheaza in timpul upload-ului.
  useEffect(() => {
    return () => {
      latest.current.forEach(p => {
        if (p.previewUrl.startsWith('blob:')) URL.revokeObjectURL(p.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ruleaza o singura data, la unmount; citeste `latest.current` la momentul cleanup-ului
  }, []);

  const remaining = Math.max(0, max - photos.length);
  const isFull = remaining === 0;

  const handleFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []).filter(
      f => f.type.startsWith('image/') || (acceptPdf && f.type === 'application/pdf')
    );
    e.target.value = ''; // permite reselectarea aceluiași fișier
    if (files.length === 0 || disabled) return;

    const picked: PickedPhoto[] = files.slice(0, remaining).map(file => ({
      localId: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      // Un PDF nu are miniatura: fara blob URL (n-ar fi afisat nicaieri si ar trebui revocat degeaba).
      previewUrl: file.type === 'application/pdf' ? '' : URL.createObjectURL(file),
      status: 'pending',
      progress: 0,
    }));
    dispatch({ type: 'add', photos: picked, max });

    await Promise.all(
      picked.map(async p => {
        dispatch({ type: 'progress', localId: p.localId, progress: 0 });
        try {
          const { fileId } = await upload(p.file!, pct => dispatch({ type: 'progress', localId: p.localId, progress: pct }));
          dispatch({ type: 'done', localId: p.localId, fileId });
          // Reducerul a mutat deja previewUrl pe fileId (cand arata ca URL) — blob-ul vechi al lui
          // `p` nu mai e afisat nicaieri, deci se poate revoca.
          if (p.previewUrl.startsWith('blob:')) URL.revokeObjectURL(p.previewUrl);
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : 'Încărcarea a eșuat';
          dispatch({ type: 'error', localId: p.localId, error: message });
        }
      })
    );
  };

  const handleRemove = (p: PickedPhoto) => {
    if (p.previewUrl.startsWith('blob:')) URL.revokeObjectURL(p.previewUrl);
    dispatch({ type: 'remove', localId: p.localId });
  };

  const grid = (
    <ul className="grid grid-cols-2 sm:grid-cols-3 gap-3" aria-label={acceptPdf ? 'Fișiere selectate' : 'Fotografii selectate'}>
      {photos.map((p, index) => {
        const ePdf = esteDovadaPdf(p);
        const eticheta = ePdf ? `fișierul ${index + 1}` : `fotografia ${index + 1}`;
        return (
        <li key={p.localId} className="relative aspect-square rounded-lg overflow-hidden border border-border bg-muted">
          {ePdf ? (
            // Task 15: un PDF nu e imagine — icon + numele fisierului, niciodata `<img>`.
            // `shrink-0` pe nume: altfel flex-ul il strivea pe un rand si al doilea se suprapunea (captura 360).
            // Cu eroare, numele se muta in banda de eroare (mai jos), ca omul sa vada CARE fisier a picat.
            <div className="flex h-full w-full flex-col items-center justify-start gap-2 px-3 pt-4 text-center">
              <FileText className="size-10 shrink-0 text-magenta" aria-hidden="true" />
              {p.status !== 'error' && (
                <span className="w-full shrink-0 text-sm font-medium text-foreground line-clamp-2 break-words" title={numeDovada(p)}>
                  {numeDovada(p)}
                </span>
              )}
            </div>
          ) : (
            <img src={p.previewUrl} alt={`Fotografie ${index + 1}`} className="w-full h-full object-cover" />
          )}

          {p.status === 'uploading' && (
            <div className="absolute inset-x-0 bottom-0 bg-white/85 px-2 py-1">
              <div
                className="h-1.5 rounded-full bg-muted overflow-hidden"
                role="progressbar"
                aria-label={`Încărcare ${eticheta}`}
                aria-valuenow={p.progress}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="h-full bg-magenta transition-[width]" style={{ width: `${p.progress}%` }} />
              </div>
              <p className="text-xs text-foreground tabular-nums mt-0.5">{p.progress}%</p>
            </div>
          )}

          {p.status === 'error' && (
            <div className="absolute inset-x-0 bottom-0 bg-destructive/90 text-white px-2 py-1 flex items-center gap-1" role="alert">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {/* Cu PDF-uri, mesajele serverului (tip/marime) sunt mai lungi: se rup pe randuri, intregi, nu se taie. */}
              <span className={cn('text-xs', acceptPdf ? 'min-w-0 break-words' : 'truncate')} title={p.error}>
                {ePdf && <span className="block truncate font-semibold">{numeDovada(p)}</span>}
                {p.error ?? 'Eroare'}
              </span>
            </div>
          )}

          {!disabled && (
            <button
              type="button"
              onClick={() => handleRemove(p)}
              aria-label={`Șterge ${eticheta}`}
              className="absolute top-1 right-1 touch-target flex items-center justify-center rounded-full bg-white/90 text-foreground shadow"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </li>
        );
      })}
    </ul>
  );

  return (
    <div className="space-y-4">
      {photos.length > 0 && (
        constrainGrid ? (
          // EXCEPTIE de la R4, doar cand parintele o cere explicit (vezi comentariul pe prop):
          // max-h + overflow fac DOAR grila derulabila, ca butoanele de mai jos si orice CTA din
          // afara componentei sa ramana mereu vizibile, fara sa impinga pagina la scroll.
          <div className="max-h-[38dvh] overflow-y-auto">{grid}</div>
        ) : grid
      )}

      <div className="space-y-2">
        <button
          type="button"
          disabled={disabled || isFull}
          onClick={() => cameraRef.current?.click()}
          className={cn(
            'w-full min-h-14 flex items-center justify-center gap-2 rounded-lg font-medium',
            'bg-magenta text-white hover:bg-magenta-hover disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <Camera className="w-5 h-5" />
          Fă o poză
        </button>
        <button
          type="button"
          disabled={disabled || isFull}
          onClick={() => galleryRef.current?.click()}
          className={cn(
            'w-full min-h-14 flex items-center justify-center gap-2 rounded-lg font-medium',
            'border-2 border-magenta text-magenta bg-white hover:bg-lilac/30 disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <Images className="w-5 h-5" />
          Alege din galerie
        </button>

        {/* Cameră: un singur cadru, camera din spate. Galerie: mai multe, fără capture. */}
        <input ref={cameraRef} type="file" accept={ACCEPT} capture="environment" className="hidden" onChange={handleFiles} />
        <input ref={galleryRef} type="file" accept={acceptPdf ? ACCEPT_CU_PDF : ACCEPT} multiple className="hidden" onChange={handleFiles} />
      </div>

      <p className="text-sm text-muted-foreground tabular-nums" aria-live="polite">
        {photos.length} din maximum {max} {acceptPdf ? 'fișiere' : 'fotografii'}
      </p>
    </div>
  );
}

export default PhotoPicker;
